import type { Assignment, AssignmentSubmission, AssignmentSubmissionType } from './model';

export interface AssignmentListItem {
  readonly assignmentId: string;
  readonly pieceName: string;
  readonly dueAt: string;
  readonly submissionType: AssignmentSubmissionType;
  readonly mySubmissionStatus: 'not_submitted' | 'submitted' | 'ended';
  readonly submittedCount: number;
  readonly expectedSubmitterCount: number;
  readonly deliveryRuleCount: number;
  readonly canManage: boolean;
}

export interface AssignmentDetail extends AssignmentListItem {
  readonly choirId: string;
  readonly seasonId: string;
  readonly status: Assignment['status'];
  readonly description: string;
  readonly deliveryRules: readonly {
    readonly assignmentDeliveryRuleId: string;
    readonly targetSummary: string;
    readonly reviewerSummary: string;
    readonly target?: {
      readonly memberGroupIds: readonly string[];
      readonly includeSeasonMembershipIds: readonly string[];
      readonly excludeSeasonMembershipIds: readonly string[];
    };
    readonly reviewers?: {
      readonly memberGroupIds: readonly string[];
      readonly memberGroupTagIds: readonly string[];
      readonly seasonMembershipIds: readonly string[];
    };
  }[];
  readonly mySubmission: null | {
    readonly assignmentSubmissionId: string;
    readonly submittedAt: string;
    readonly fileName: string;
  };
  readonly submissionRows: readonly {
    readonly seasonMembershipId: string;
    readonly nickname: string;
    readonly memberGroupNames: readonly string[];
    readonly status: 'not_submitted' | 'submitted';
    readonly submittedAt: string | null;
  }[];
  readonly canSubmit: boolean;
  readonly canOverwrite: boolean;
}

export interface ResolvedAssignmentDeliveryRuleView {
  readonly submitterIds: readonly string[];
  readonly reviewerIds: readonly string[];
}

export interface AssignmentViewMember {
  readonly seasonMembershipId: string;
  readonly nickname: string;
  readonly memberGroupNames: readonly string[];
}

export interface AssignmentDeliveryRuleViewSource {
  readonly assignmentDeliveryRuleId: string;
  readonly targetSummary: string;
  readonly reviewerSummary: string;
  readonly target?: {
    readonly memberGroupIds: readonly string[];
    readonly includeSeasonMembershipIds: readonly string[];
    readonly excludeSeasonMembershipIds: readonly string[];
  };
  readonly reviewers?: {
    readonly memberGroupIds: readonly string[];
    readonly memberGroupTagIds: readonly string[];
    readonly seasonMembershipIds: readonly string[];
  };
}

export function projectAssignmentListItem(input: {
  readonly assignment: Assignment;
  readonly currentSeasonMembershipId: string;
  readonly canManage: boolean;
  readonly nowIso: string;
  readonly resolvedDeliveryRules: readonly ResolvedAssignmentDeliveryRuleView[];
  readonly submissions: readonly AssignmentSubmission[];
}): AssignmentListItem {
  const scopedSubmissions = filterAssignmentSubmissions(input.assignment, input.submissions);
  const expectedSubmitterIds = collectExpectedSubmitterIds(input.resolvedDeliveryRules);
  const submittedSubmitterIds = collectSubmittedSubmitterIds(scopedSubmissions, expectedSubmitterIds);
  const mySubmission = findSubmission(scopedSubmissions, input.currentSeasonMembershipId);

  return {
    assignmentId: input.assignment.assignmentId,
    pieceName: input.assignment.pieceName,
    dueAt: input.assignment.dueAt,
    submissionType: input.assignment.submissionType,
    mySubmissionStatus: getMySubmissionStatus({
      assignment: input.assignment,
      mySubmission,
      nowIso: input.nowIso
    }),
    submittedCount: submittedSubmitterIds.size,
    expectedSubmitterCount: expectedSubmitterIds.length,
    deliveryRuleCount: input.resolvedDeliveryRules.length,
    canManage: input.canManage
  };
}

export function projectAssignmentDetail(input: {
  readonly assignment: Assignment;
  readonly currentSeasonMembershipId: string;
  readonly canManage: boolean;
  readonly nowIso: string;
  readonly members: readonly AssignmentViewMember[];
  readonly resolvedDeliveryRules: readonly ResolvedAssignmentDeliveryRuleView[];
  readonly deliveryRules: readonly AssignmentDeliveryRuleViewSource[];
  readonly submissions: readonly AssignmentSubmission[];
}): AssignmentDetail {
  const scopedSubmissions = filterAssignmentSubmissions(input.assignment, input.submissions);
  const listItem = projectAssignmentListItem({ ...input, submissions: scopedSubmissions });
  const expectedSubmitterIds = collectExpectedSubmitterIds(input.resolvedDeliveryRules);
  const expectedSubmitterIdSet = new Set(expectedSubmitterIds);
  const submissionsBySubmitter = new Map(
    scopedSubmissions.map((submission) => [submission.submitterSeasonMembershipId, submission])
  );
  const mySubmission = findSubmission(scopedSubmissions, input.currentSeasonMembershipId);
  const canSubmit =
    !isAssignmentEnded(input.nowIso, input.assignment.dueAt) &&
    expectedSubmitterIdSet.has(input.currentSeasonMembershipId);

  return {
    ...listItem,
    choirId: input.assignment.choirId,
    seasonId: input.assignment.seasonId,
    status: input.assignment.status,
    description: input.assignment.description,
    deliveryRules: input.deliveryRules.map((deliveryRule) => projectDeliveryRule(deliveryRule, input.canManage)),
    mySubmission:
      mySubmission === null
        ? null
        : {
            assignmentSubmissionId: mySubmission.assignmentSubmissionId,
            submittedAt: mySubmission.submittedAt,
            fileName: mySubmission.fileName
          },
    submissionRows: input.members
      .filter((member) => expectedSubmitterIdSet.has(member.seasonMembershipId))
      .map((member) => {
        const submission = submissionsBySubmitter.get(member.seasonMembershipId);
        return {
          seasonMembershipId: member.seasonMembershipId,
          nickname: member.nickname,
          memberGroupNames: member.memberGroupNames,
          status: submission ? ('submitted' as const) : ('not_submitted' as const),
          submittedAt: submission?.submittedAt ?? null
        };
      }),
    canSubmit,
    canOverwrite: canSubmit && mySubmission !== null
  };
}

function projectDeliveryRule(
  deliveryRule: AssignmentDeliveryRuleViewSource,
  canManage: boolean
): AssignmentDetail['deliveryRules'][number] {
  const projected = {
    assignmentDeliveryRuleId: deliveryRule.assignmentDeliveryRuleId,
    targetSummary: deliveryRule.targetSummary,
    reviewerSummary: deliveryRule.reviewerSummary
  };

  if (!canManage) {
    return projected;
  }

  return {
    ...projected,
    target: deliveryRule.target,
    reviewers: deliveryRule.reviewers
  };
}

function filterAssignmentSubmissions(
  assignment: Assignment,
  submissions: readonly AssignmentSubmission[]
): readonly AssignmentSubmission[] {
  return submissions.filter(
    (submission) =>
      submission.assignmentId === assignment.assignmentId &&
      submission.choirId === assignment.choirId &&
      submission.seasonId === assignment.seasonId
  );
}

function collectExpectedSubmitterIds(
  resolvedDeliveryRules: readonly ResolvedAssignmentDeliveryRuleView[]
): readonly string[] {
  const seen = new Set<string>();
  const submitterIds: string[] = [];

  for (const deliveryRule of resolvedDeliveryRules) {
    for (const submitterId of deliveryRule.submitterIds) {
      if (!seen.has(submitterId)) {
        seen.add(submitterId);
        submitterIds.push(submitterId);
      }
    }
  }

  return submitterIds;
}

function collectSubmittedSubmitterIds(
  submissions: readonly AssignmentSubmission[],
  expectedSubmitterIds: readonly string[]
): ReadonlySet<string> {
  const expectedSubmitterIdSet = new Set(expectedSubmitterIds);
  const submittedSubmitterIds = new Set<string>();

  for (const submission of submissions) {
    if (expectedSubmitterIdSet.has(submission.submitterSeasonMembershipId)) {
      submittedSubmitterIds.add(submission.submitterSeasonMembershipId);
    }
  }

  return submittedSubmitterIds;
}

function findSubmission(
  submissions: readonly AssignmentSubmission[],
  seasonMembershipId: string
): AssignmentSubmission | null {
  return submissions.find((submission) => submission.submitterSeasonMembershipId === seasonMembershipId) ?? null;
}

function getMySubmissionStatus(input: {
  readonly assignment: Assignment;
  readonly mySubmission: AssignmentSubmission | null;
  readonly nowIso: string;
}): AssignmentListItem['mySubmissionStatus'] {
  if (input.mySubmission !== null) {
    return 'submitted';
  }

  return isAssignmentEnded(input.nowIso, input.assignment.dueAt) ? 'ended' : 'not_submitted';
}

function isAssignmentEnded(nowIso: string, dueAt: string): boolean {
  return parseAssignmentTime(nowIso) >= parseAssignmentTime(dueAt);
}

function parseAssignmentTime(value: string): number {
  const time = Date.parse(value);
  if (Number.isNaN(time)) {
    throw new Error(`Invalid assignment timestamp: ${value}`);
  }
  return time;
}
